import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { LogHelper } from 'app/core/helpers/log.helper';
import { AssignCardRequest, CaxtonAssignCardRequest, Gender, PatientBasicInfo } from 'app/core/models/patient.model';
import { SelectOption } from 'app/core/models/select-option.model';
import { PatientService } from 'app/core/services/patient.service';
import { AlertService } from 'app/shared/alert/alert.service';
import { DropdownInputComponent } from 'app/shared/dropdown-input/dropdown-input.component';
import { ModalComponent } from 'app/shared/modal/modal.component';

@Component({
  selector: 'app-grant-online-access-to-caxton',
  templateUrl: './grant-online-access-to-caxton.component.html',
  styleUrls: ['./grant-online-access-to-caxton.component.scss']
})
export class GrantOnlineAccessToCaxtonComponent {
  @ViewChild("confirmGrantOnlineAccessToCaxtonModal") confirmGrantOnlineAccessToCaxtonModal: ModalComponent;
  @ViewChild("grantOnlineAccessToCaxtonFormModal") grantOnlineAccessToCaxtonFormModal: ModalComponent;
  @ViewChild("genderSelect") genderSelect: DropdownInputComponent;
  @ViewChild("dob") dob: ElementRef;

  @Input() patientId: string;
  @Input() patientBasicInfo: PatientBasicInfo;
  @Input() paymentInfoForm: FormGroup;

  @Output('caxtonOnlineAccessGranted') caxtonOnlineAccessGranted = new EventEmitter<boolean>();

  genderOptions: SelectOption[] = Gender.all().map((x) => ({ value: x, text: x } as SelectOption));
  isCaxtonAccessProcessing: boolean = false;

  caxtonOnlineAccessForm: FormGroup = new FormGroup({
    firstName: new FormControl("", [
      Validators.required,
      Validators.pattern("^[A-Za-z ]+$"),
    ]),
    lastName: new FormControl("", [
      Validators.required,
      Validators.pattern("^[A-Za-z ]+$"),
    ]),
    email: new FormControl(""),
    gender: new FormControl("", [Validators.required]),
    dateOfBirth: new FormControl("")
  });

  defaultDOB: string = "01/01/1970";

  constructor(
    private patientService: PatientService,
    private alertService: AlertService
  ) { }

  onConfirmGrantAccess() {
    this.confirmGrantOnlineAccessToCaxtonModal.hide();
    this.loadGrantCaxtonOnlineAccessForm();
    this.grantOnlineAccessToCaxtonFormModal.show();
  }

  loadGrantCaxtonOnlineAccessForm() {
    this.caxtonOnlineAccessForm.patchValue({ gender: Gender.Other });
    if (this.patientBasicInfo) {
      this.caxtonOnlineAccessForm.patchValue({
        email: this.patientBasicInfo.email,
        firstName: this.patientBasicInfo.firstName,
        lastName: this.patientBasicInfo.lastName,
      });
    }
    this.genderSelect.setValue(Gender.Other);
    this.dob.nativeElement.value = this.defaultDOB;
  }

  onGrantOnlineAccessToCaxtonFormSubmit() {
    this.isCaxtonAccessProcessing = true;
    this.loadGrantCaxtonOnlineAccessFormDates();

    let walletId = this.paymentInfoForm.get("caxtonWalletId").value;
    let walletName = this.paymentInfoForm.get("caxtonWalletName").value;

    let caxtonAssignCardRequest = this.caxtonOnlineAccessForm.value as CaxtonAssignCardRequest;
    let assignCardRequest: AssignCardRequest = {
      patientTrialId: this.patientId,
      walletId: walletId,
      caxtonRequest: caxtonAssignCardRequest,
    };

    this.patientService.grantOnlineAccess(this.patientId, assignCardRequest).subscribe({
        next: (result) => {
          this.isCaxtonAccessProcessing = false;
          this.caxtonOnlineAccessGranted.emit(result.onlineAccessGranted);
          this.grantOnlineAccessToCaxtonFormModal.hide();
          this.alertService.showSuccessAlert(`You have successfully granted online access to cards inside wallet ${walletName}`);
        },
        error: (error) => {
          this.alertService.showWarningAlert(error.error ? error.error.title : "Failed to grant online access to cards in this wallet. Please try again or contact support.");
          this.isCaxtonAccessProcessing = false;
          LogHelper.log(error);
        },
      });
  }

  loadGrantCaxtonOnlineAccessFormDates() {
    this.caxtonOnlineAccessForm.patchValue({
      dateOfBirth: this.dob.nativeElement.value,
    });
  }
}
